// Services d'authentification Firebase avec mode démo
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from './config';

// Utilisateurs de démonstration (quand Firebase n'est pas configuré)
const DEMO_USERS = [
  {
    id: 'admin-kila',
    email: 'contact@kila-france.com',
    password: 'b12As5o3**!!2024',
    name: 'Admin KILA France',
    role: 'admin'
  },
  {
    id: 'demo-client',
    email: 'client@demo.com',
    password: 'demo123',
    name: 'Client Démo',
    role: 'client'
  }
];

// Vérifier si Firebase est configuré
const isFirebaseConfigured = () => {
  try {
    return auth && auth.app && auth.app.options.apiKey !== 'demo-api-key';
  } catch {
    return false;
  }
};

// Mode démo - authentification simulée
const demoAuth = {
  login: async (email, password) => {
    await new Promise(resolve => setTimeout(resolve, 1000)); // Simuler délai
    
    const user = DEMO_USERS.find(u => u.email === email && u.password === password);
    if (!user) {
      throw { code: 'auth/user-not-found', message: 'Utilisateur non trouvé' };
    }
    
    return {
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role
    };
  },
  
  register: async (email, password, userData) => {
    await new Promise(resolve => setTimeout(resolve, 1000)); // Simuler délai
    
    const existingUser = DEMO_USERS.find(u => u.email === email);
    if (existingUser) {
      throw { code: 'auth/email-already-in-use', message: 'Email déjà utilisé' };
    }
    
    return {
      id: 'new-user-' + Date.now(),
      name: userData.name,
      email: userData.email,
      role: userData.role || 'client'
    };
  }
};

// Inscription utilisateur
export const registerUser = async (email, password, userData) => {
  try {
    console.log('📝 Inscription Firebase pour:', email);
    
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Mise à jour du profil
    await updateProfile(user, {
      displayName: userData.name
    });

    // Sauvegarde des données utilisateur dans Firestore
    await setDoc(doc(db, 'users', user.uid), {
      name: userData.name,
      email: userData.email,
      role: userData.role || 'client',
      createdAt: new Date()
    });

    console.log('✅ Utilisateur inscrit:', userData.name);

    return {
      id: user.uid,
      name: userData.name,
      email: userData.email,
      role: userData.role || 'client'
    };
  } catch (error) {
    console.error('❌ Erreur lors de l\'inscription Firebase:', error);
    throw error;
  }
};

// Créer l'admin KILA France automatiquement
export const createAdminIfNotExists = async () => {
  try {
    const adminEmail = 'contact@kila-france.com';
    const adminPassword = 'b12As5o3**!!2024';
    
    console.log('🔍 Vérification existence admin KILA France...');
    
    // Essayer de créer le compte admin
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, adminEmail, adminPassword);
      const user = userCredential.user;
      
      // Mettre à jour le profil
      await updateProfile(user, {
        displayName: 'Admin KILA France'
      });
      
      // Créer le document utilisateur dans Firestore avec rôle admin
      const adminData = {
        name: 'Admin KILA France',
        email: adminEmail,
        role: 'admin',
        createdAt: new Date(),
        isDefaultAdmin: true
      };
      
      await setDoc(doc(db, 'users', user.uid), adminData);
      
      console.log('✅ Compte admin KILA France créé avec succès !');
      return true;
    } catch (createError) {
      if (createError.code === 'auth/email-already-in-use') {
        console.log('ℹ️ Admin KILA France existe déjà');
        return true;
      } else {
        console.error('❌ Erreur création admin:', createError);
        return false;
      }
    }
  } catch (error) {
    console.error('❌ Erreur lors de l\'initialisation admin:', error);
    return false;
  }
};

// Connexion utilisateur
export const loginUser = async (email, password) => {
  try {
    // S'assurer que l'admin existe avant toute connexion
    if (email === 'contact@kila-france.com') {
      await createAdminIfNotExists();
    }
    
    // Utiliser Firebase réel maintenant que c'est configuré
    console.log('🔐 Tentative de connexion Firebase avec:', email);
    
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Récupérer les données utilisateur depuis Firestore
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      console.log('✅ Utilisateur connecté:', userData.name, '- Rôle:', userData.role);
      
      return {
        id: user.uid,
        name: userData.name,
        email: userData.email,
        role: userData.role
      };
    } else {
      // Utilisateur n'existe pas dans Firestore, créer le document
      const newUserData = {
        name: user.displayName || email.split('@')[0],
        email: user.email,
        role: 'client',
        createdAt: new Date()
      };
      
      await setDoc(doc(db, 'users', user.uid), newUserData);
      
      return {
        id: user.uid,
        name: newUserData.name,
        email: newUserData.email,
        role: newUserData.role
      };
    }
  } catch (error) {
    console.error('❌ Erreur de connexion Firebase:', error);
    throw error;
  }
};

// Déconnexion
export const logoutUser = async () => {
  try {
    if (isFirebaseConfigured()) {
      await signOut(auth);
    }
    
    // Nettoyer localStorage dans tous les cas
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  } catch (error) {
    console.error('Erreur lors de la déconnexion:', error);
    throw error;
  }
};

// Observer l'état d'authentification
export const onAuthChange = (callback) => {
  if (!isFirebaseConfigured()) {
    // Mode démo - vérifier localStorage
    const checkLocalStorage = () => {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          callback(JSON.parse(storedUser));
        } catch {
          callback(null);
        }
      } else {
        callback(null);
      }
    };
    
    checkLocalStorage();
    
    // Retourner une fonction de nettoyage factice
    return () => {};
  }

  return onAuthStateChanged(auth, async (user) => {
    if (user) {
      // Récupérer les données depuis Firestore
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        callback({
          id: user.uid,
          name: userData.name || user.displayName,
          email: user.email,
          role: userData.role || 'client',
          subscription: userData.subscription
        });
      } else {
        callback(null);
      }
    } else {
      callback(null);
    }
  });
};